import { Head, router } from "@inertiajs/react"
import { Check, Copy } from "lucide-react"
import { useMemo, useState } from "react"

import { HpiFilterSelect } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { teamSignaturePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface SignatureData {
  id: number
  name: string
  body: string | null
}

interface EmployeeData {
  id: number
  name: string
  formal_name: string | null
  job_title: string | null
  cell_phone: string | null
  email: string | null
  sig_show_cell: boolean
  sig_profile: string | null
  sig_vcard: string | null
}

interface LocationOption {
  id: number
  name: string
}

interface EmployeeOption {
  id: number
  name: string
}

interface Props {
  signature: SignatureData
  employee: EmployeeData
  locations: LocationOption[]
  selected_location_id: number
  selected_employee_id: number
  can_select_employee: boolean
  employees?: EmployeeOption[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatPhone(phone: string | null): string {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

function renderSignature(body: string | null, employee: EmployeeData): string {
  if (!body) return ""
  let html = body
  html = html.replace(/\{\{empName\}\}/g, employee.formal_name ?? employee.name)
  html = html.replace(/\{\{empTitle\}\}/g, employee.job_title ?? "")

  if (employee.sig_show_cell && employee.cell_phone) {
    html = html.replace(
      /\{\{empCell\}\}/g,
      ` <span style="color:#FDBA2F;">|</span> Cell: <a href="tel:${employee.cell_phone}" style="color:#547099;text-decoration:none;font-weight:normal;">${formatPhone(employee.cell_phone)}</a>`,
    )
  } else {
    html = html.replace(/\{\{empCell\}\}/g, "")
  }

  if (employee.email?.toLowerCase().includes("hawkpi.com")) {
    html = html.replace(/\{\{empEmail\}\}/g, employee.email)
  } else {
    html = html.replace(/\{\{empEmail\}\}/g, "")
  }

  if (employee.sig_profile) {
    html = html.replace(
      /\{\{empProfile\}\}/g,
      ` <span style="color:#FDBA2F;">|</span> <a href="${employee.sig_profile}" style="color:#547099;text-decoration:none;font-weight:normal;">View Professional Bio</a>`,
    )
  } else {
    html = html.replace(/\{\{empProfile\}\}/g, "")
  }

  if (employee.sig_vcard) {
    html = html.replace(
      /\{\{empVCard\}\}/g,
      `<a href="${employee.sig_vcard}" style="color:#547099;text-decoration:none;font-weight:normal;">Add to Contacts</a>`,
    )
  } else {
    html = html.replace(/\{\{empVCard\}\}/g, "")
  }

  return html
}

/* ── Constants ────────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team", href: "/team" },
  { title: "Email Signature", href: "/team/signature" },
]

/* ── Main Component ───────────────────────────────────── */

export default function TeamSignature({
  signature,
  employee,
  locations,
  selected_location_id,
  selected_employee_id,
  can_select_employee,
  employees = [],
}: Props) {
  const [copied, setCopied] = useState(false)

  const renderedHtml = useMemo(
    () => renderSignature(signature.body, employee),
    [signature.body, employee],
  )

  function handleLocationChange(value: string) {
    router.get(
      teamSignaturePath(),
      {
        sig: value,
        employee_id: can_select_employee ? selected_employee_id : undefined,
      },
      { preserveState: true },
    )
  }

  function handleEmployeeChange(value: string) {
    router.get(
      teamSignaturePath(),
      { sig: selected_location_id, employee_id: value },
      { preserveState: true },
    )
  }

  async function handleCopy() {
    try {
      const blob = new Blob([renderedHtml], { type: "text/html" })
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": blob }),
      ])
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: copy as plain text
      const tmp = document.createElement("div")
      tmp.innerHTML = renderedHtml
      await navigator.clipboard.writeText(tmp.textContent ?? "")
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Email Signature" />

      <div className="p-6">
        {/* Filters */}
        {(can_select_employee || locations.length > 1) && (
          <div className="mb-6 flex flex-wrap items-center gap-4">
            {can_select_employee && employees.length > 0 && (
              <HpiFilterSelect
                label="Employee"
                value={String(selected_employee_id)}
                onChange={handleEmployeeChange}
                options={employees.map((emp) => ({
                  value: emp.id,
                  label: emp.name,
                }))}
              />
            )}
            {locations.length > 1 && (
              <HpiFilterSelect
                label="Location"
                value={String(selected_location_id)}
                onChange={handleLocationChange}
                options={locations.map((loc) => ({
                  value: loc.id,
                  label: loc.name,
                }))}
              />
            )}
            <button
              onClick={() => {
                void handleCopy()
              }}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium text-white"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? "Copied!" : "Copy Signature"}
            </button>
          </div>
        )}

        {/* No filters case - just show copy button */}
        {!can_select_employee && locations.length <= 1 && (
          <div className="mb-6">
            <button
              onClick={() => {
                void handleCopy()
              }}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium text-white"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? "Copied!" : "Copy Signature"}
            </button>
          </div>
        )}

        {/* Signature preview */}
        <div className="border-hpi-light-gray rounded-lg border bg-white p-6">
          <div className="text-hpi-med-blue mb-3 text-xs font-medium tracking-wider uppercase">
            Preview — {signature.name}
          </div>
          <div
            className="signature-preview"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        </div>

        {/* Instructions */}
        <div className="border-hpi-light-gray bg-hpi-pale-blue mt-6 rounded-lg border p-4">
          <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
            How to use
          </h4>
          <ol className="text-hpi-dark-gray list-decimal space-y-1 pl-5 text-sm">
            <li>Open Portal in another browser tab</li>
            <li>Click the Settings gear icon on the top bar</li>
            <li>Click View all Outlook Settings near the bottom</li>
            <li>Click Compose and Reply</li>
            <li>Delete anything in the text field for Email Signature</li>
            <li>Click Copy above to copy signature to clipboard</li>
            <li>Switch back to browser tab with Portal open</li>
            <li>
              Click in Email Signature text field, press Ctrl-V to paste
              signature in
            </li>
            <li>Click Save, close settings</li>
            <li>Create a new message and send it to yourself to test it</li>
          </ol>
        </div>
      </div>
    </AppLayout>
  )
}
